/**
 * The device location a worker shares on the jobs board ("Closest to Me",
 * "Show distance from me") is kept as an APPROXIMATE last-known location so
 * we can match them with nearby work when they have no address on file
 * (Greg 2026-10-12). The server decides what it is used for and never lets
 * it override a real address — see functions/src/users/workerLocationHint.ts.
 *
 * Signed in → sent straight to the `saveWorkerLocationHint` callable.
 * Guest → parked in localStorage (24h, same shape as pendingClaim.ts) and
 * sent once they sign in or create an account (`flushPendingLocationHint`).
 * Flutter twin: lib/features/jobs/data/location_hint_service.dart in c1_app.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';

import { auth } from '../firebase';

export interface PendingLocationHint {
  lat: number;
  lng: number;
  accuracyM: number | null;
  savedAt: number;
}

const KEY = 'c1.pendingLocationHint.v1';
export const PENDING_LOCATION_TTL_MS = 24 * 60 * 60 * 1000;

export function isUsableCoords(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0)
  );
}

export function isPendingLocationHintValid(p: unknown, nowMs: number = Date.now()): p is PendingLocationHint {
  if (!p || typeof p !== 'object') return false;
  const h = p as Partial<PendingLocationHint>;
  return isUsableCoords(h.lat, h.lng) && typeof h.savedAt === 'number' && nowMs - h.savedAt >= 0 && nowMs - h.savedAt <= PENDING_LOCATION_TTL_MS;
}

export function loadPendingLocationHint(nowMs: number = Date.now()): PendingLocationHint | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return isPendingLocationHintValid(parsed, nowMs) ? parsed : null;
  } catch {
    return null;
  }
}

function clearPendingLocationHint(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

async function send(hint: { lat: number; lng: number; accuracyM: number | null }): Promise<boolean> {
  try {
    await httpsCallable(getFunctions(), 'saveWorkerLocationHint')({ ...hint, source: 'device_web' });
    return true;
  } catch {
    return false; // never let a location save break the page the worker is using
  }
}

/** Call with the position the browser just returned. Safe to call for guests and signed-in workers alike. */
export async function rememberSharedLocation(coords: { latitude: number; longitude: number; accuracy?: number | null }, nowMs: number = Date.now()): Promise<void> {
  if (!isUsableCoords(coords.latitude, coords.longitude)) return;
  const hint = { lat: coords.latitude, lng: coords.longitude, accuracyM: typeof coords.accuracy === 'number' && Number.isFinite(coords.accuracy) ? coords.accuracy : null };
  if (auth.currentUser) {
    await send(hint);
    return;
  }
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...hint, savedAt: nowMs }));
  } catch {
    /* private mode / storage full — the sort still works, we just cannot keep it */
  }
}

/** After sign-in or signup: send a location the visitor shared while still a guest. */
export async function flushPendingLocationHint(nowMs: number = Date.now()): Promise<void> {
  if (!auth.currentUser) return;
  const pending = loadPendingLocationHint(nowMs);
  if (!pending) {
    clearPendingLocationHint(); // drops an expired or malformed entry
    return;
  }
  if (await send({ lat: pending.lat, lng: pending.lng, accuracyM: pending.accuracyM })) clearPendingLocationHint();
}
